import { naarPlatteTekst } from "@/lib/rich-text";
import { RichText } from "@/components/site/RichText";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ArrowRight, Calendar, Layers, MapPin, Ruler } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LocaleLink } from "@/components/site/LocaleLink";
import { PageHero } from "@/components/site/PageHero";
import { Section } from "@/components/site/Section";
import { Overline } from "@/components/site/Overline";
import { ResponsiveImage } from "@/components/site/ResponsiveImage";
import { CtaSplit } from "@/components/site/CtaSplit";
import { FaqSection } from "@/components/site/FaqSection";
import { publishedProjectQuery } from "@/lib/projects-queries";
import { projectTypeLabel, projectTypeService } from "@/content/project-types";
import { canonical, SITE_URL, shareImageMetaFromPath, langPageMeta } from "@/content/site";
import type { ProjectDetail, ProjectPhoto } from "@/lib/projects.functions";

export const Route = createFileRoute("/$lang/realisaties/$slug")({
  staticData: { sitemap: false },
  loader: async ({ context, params }) => {
    const project = await context.queryClient.ensureQueryData(publishedProjectQuery(params.slug, params.lang));
    if (!project) throw notFound();
    return project;
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return { meta: [{ title: "Realisatie niet gevonden" }, { name: "robots", content: "noindex" }] };
    }
    const project = loaderData;
    const path = `/realisaties/${project.slug}`;
    const title = project.metaTitle || `${project.titel} — Gopro Projects`;
    const description = project.metaDescription || project.samenvatting;
    const hero = project.photos.find((photo) => photo.role === "hoofdbeeld") ?? project.photos[0];
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        ...langPageMeta(params.lang, path).meta,
        { name: "twitter:card", content: "summary_large_image" },
        ...(hero ? shareImageMetaFromPath(hero.media.url, hero.media.alt) : []),
      ],
      links: langPageMeta(params.lang, path).links,
      scripts: [
        projectJsonLd(project),
        projectBreadcrumbJsonLd(project),
        ...(project.faqs.length > 0 ? [projectFaqJsonLd(project)] : []),
      ],
    };
  },
  component: ProjectDetailPage,
});

function absolute(url: string) {
  return url.startsWith("http") ? url : `${SITE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
}

function projectJsonLd(project: ProjectDetail) {
  const hero = project.photos.find((photo) => photo.role === "hoofdbeeld") ?? project.photos[0];
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "CreativeWork",
      name: project.titel,
      headline: project.titel,
      description: project.antwoordSamenvatting || project.samenvatting,
      url: canonical(`/nl/realisaties/${project.slug}`),
      inLanguage: project.lang,
      dateCreated: project.uitvoeringsjaar ? String(project.uitvoeringsjaar) : undefined,
      creator: { "@id": `${SITE_URL}/#organization` },
      provider: { "@id": `${SITE_URL}/#organization` },
      about: projectTypeLabel(project.typeWerk),
      material: project.gebruikteMaterialen.length > 0 ? project.gebruikteMaterialen : undefined,
      contentLocation: project.gemeente
        ? {
            "@type": "Place",
            name: project.gemeente,
            address: {
              "@type": "PostalAddress",
              addressLocality: project.gemeente,
              addressRegion: project.provincie || undefined,
              addressCountry: "BE",
            },
          }
        : undefined,
      image: hero
        ? {
            "@type": "ImageObject",
            url: absolute(hero.media.url),
            caption: hero.media.alt,
            width: hero.media.width ?? undefined,
            height: hero.media.height ?? undefined,
          }
        : undefined,
    }),
  };
}

function projectBreadcrumbJsonLd(project: ProjectDetail) {
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: canonical("/nl") },
        { "@type": "ListItem", position: 2, name: "Realisaties", item: canonical("/nl/realisaties") },
        {
          "@type": "ListItem",
          position: 3,
          name: project.titel,
          item: canonical(`/nl/realisaties/${project.slug}`),
        },
      ],
    }),
  };
}

function projectFaqJsonLd(project: ProjectDetail) {
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: project.faqs.map((faq) => ({
        "@type": "Question",
        name: faq.vraag,
        acceptedAnswer: { "@type": "Answer", text: naarPlatteTekst(faq.antwoord) },
      })),
    }),
  };
}

/** Voor-na-paren: elk 'voor'-beeld met het beeld waarnaar het verwijst. */
function beforeAfterPairs(photos: ProjectPhoto[]) {
  const byMediaId = new Map(photos.map((photo) => [photo.mediaId, photo]));
  return photos
    .filter((photo) => photo.role === "voor" && photo.pairedMediaId)
    .map((before) => ({ before, after: byMediaId.get(before.pairedMediaId!) }))
    .filter((pair): pair is { before: ProjectPhoto; after: ProjectPhoto } => pair.after !== undefined);
}

function ProjectDetailPage() {
  const { slug, lang } = Route.useParams();
  const { data: project } = useSuspenseQuery(publishedProjectQuery(slug, lang));
  if (!project) return null;

  const hero = project.photos.find((photo) => photo.role === "hoofdbeeld") ?? project.photos[0];
  const pairs = beforeAfterPairs(project.photos);
  const pairedIds = new Set(pairs.flatMap((pair) => [pair.before.mediaId, pair.after.mediaId]));
  const gallery = project.photos.filter((photo) => photo !== hero && !pairedIds.has(photo.mediaId));
  const facts = [
    project.gemeente && { icon: MapPin, label: project.gemeente },
    project.afmetingen && { icon: Ruler, label: project.afmetingen },
    project.uitvoeringsjaar && { icon: Calendar, label: String(project.uitvoeringsjaar) },
    { icon: Layers, label: projectTypeLabel(project.typeWerk) },
  ].filter(Boolean) as { icon: typeof MapPin; label: string }[];

  return (
    <div className="flex flex-col">
      <PageHero
        title={project.titel}
        subtitle={project.samenvatting}
        imageSrc={hero?.media.url ?? ""}
        imageFocal={hero?.media.focal}
        imageAlt={hero?.media.alt ?? project.titel}
        overline={projectTypeLabel(project.typeWerk)}
        cta={{ label: "Vraag een offerte", to: "/offerte" }}
      />

      <Section tone="light">
        <div className="grid gap-12 lg:grid-cols-[1.2fr_0.8fr] lg:gap-16">
          <div>
            <Overline>Het project</Overline>
            <h2 className="mt-4 display-section">Wat we hier gedaan hebben</h2>
            {project.antwoordSamenvatting && (
              <p className="mt-6 max-w-[70ch] text-[17px] font-medium leading-7 text-on-surface-variant">
                {project.antwoordSamenvatting}
              </p>
            )}
            <RichText waarde={project.beschrijving} className="mt-6 max-w-[70ch] text-[17px] leading-7 text-on-surface-variant" />

            <div className="mt-8 flex flex-wrap gap-6 text-sm text-on-surface-variant">
              {facts.map((fact) => (
                <span key={fact.label} className="flex items-center gap-2">
                  <fact.icon className="h-5 w-5 text-brand-deep" strokeWidth={1.75} /> {fact.label}
                </span>
              ))}
            </div>

            <Button asChild variant="outline" className="mt-8">
              <LocaleLink to={projectTypeService(project.typeWerk)}>
                Meer over {projectTypeLabel(project.typeWerk).toLowerCase()} <ArrowRight className="ml-2 h-4 w-4" />
              </LocaleLink>
            </Button>
          </div>

          <div className="space-y-8">
            {project.uitgevoerdeWerken.length > 0 && (
              <div>
                <h3 className="font-display text-xl font-semibold">Uitgevoerde werken</h3>
                <ul className="mt-3 divide-y divide-border border-y border-border text-sm text-on-surface-variant">
                  {project.uitgevoerdeWerken.map((item) => (
                    <li key={item} className="py-3 leading-6">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {project.gebruikteMaterialen.length > 0 && (
              <div>
                <h3 className="font-display text-xl font-semibold">Gebruikte materialen</h3>
                <ul className="mt-3 divide-y divide-border border-y border-border text-sm text-on-surface-variant">
                  {project.gebruikteMaterialen.map((item) => (
                    <li key={item} className="py-3 leading-6">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {project.partners.length > 0 && (
              <div>
                <h3 className="font-display text-xl font-semibold">Partners</h3>
                <p className="mt-3 text-sm leading-6 text-on-surface-variant">{project.partners.join(" · ")}</p>
              </div>
            )}
          </div>
        </div>
      </Section>

      {pairs.length > 0 && (
        <Section tone="mist">
          <Overline>Voor en na</Overline>
          <h2 className="mt-4 display-section">Het verschil in hetzelfde kader</h2>
          <div className="mt-10 space-y-8">
            {pairs.map((pair) => (
              <div key={pair.before.id} className="grid gap-4 sm:grid-cols-2">
                {[
                  { label: "Voor", photo: pair.before },
                  { label: "Na", photo: pair.after },
                ].map(({ label, photo }) => (
                  <figure key={photo.id} className="relative overflow-hidden rounded-lg border border-border bg-surface">
                    <div className="aspect-[4/3]">
                      <ResponsiveImage
                        src={photo.media.url}
                        alt={photo.media.alt}
                        variants={photo.media.variants}
                        fallbackSrc={photo.media.fallbackUrl}
                        focal={photo.media.focal}
                        decorative={photo.media.decorative}
                        placeholderColor={photo.media.placeholderColor}
                        width={photo.media.width ?? 1024}
                        height={photo.media.height ?? 768}
                      />
                    </div>
                    <figcaption className="absolute left-0 top-0 bg-[color-mix(in_oklab,var(--primary)_85%,transparent)] px-4 py-2 font-display text-[13px] font-semibold uppercase tracking-[0.2em] text-primary-foreground">
                      {label}
                    </figcaption>
                  </figure>
                ))}
              </div>
            ))}
          </div>
        </Section>
      )}

      {gallery.length > 0 && (
        <Section tone="light">
          <Overline>In beeld</Overline>
          <h2 className="mt-4 display-section">Fotogalerij</h2>
          <div className="mt-10 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {gallery.map((photo) => (
              <figure key={photo.id} className="aspect-[4/3] overflow-hidden rounded-lg bg-surface">
                <ResponsiveImage
                  src={photo.media.url}
                  alt={photo.media.alt}
                  variants={photo.media.variants}
                  fallbackSrc={photo.media.fallbackUrl}
                  focal={photo.media.focal}
                  decorative={photo.media.decorative}
                  placeholderColor={photo.media.placeholderColor}
                  width={photo.media.width ?? 1024}
                  height={photo.media.height ?? 768}
                />
              </figure>
            ))}
          </div>
        </Section>
      )}

      {project.faqs.length > 0 && (
        <FaqSection
          items={project.faqs.map((faq) => ({ q: faq.vraag, a: faq.antwoord }))}
          title="Vragen over dit project"
        />
      )}

      {project.quote && project.quote.zichtbaar && (
        <Section tone="navy">
          <Overline onDark>Wat de klant zegt</Overline>
          <blockquote className="mt-6 max-w-[70ch] font-display text-2xl font-semibold leading-9">
            “{project.quote.citaat}”
          </blockquote>
          <p className="mt-4 text-[17px] text-primary-foreground/80">
            {[project.quote.naam, project.quote.gemeente].filter(Boolean).join(" — ")}
          </p>
        </Section>
      )}

      <CtaSplit
        overline="Uw project"
        title="Ook zo'n resultaat in uw tuin?"
        text="Vertel ons uw wensen en ontvang een vrijblijvende offerte op maat."
        imageSrc={hero?.media.url ?? ""}
        imageFocal={hero?.media.focal}
        imageAlt={hero?.media.alt ?? project.titel}
        ctaLabel="Offerte aanvragen"
      />
    </div>
  );
}
